import React from 'react';
import { createRoot } from 'react-dom/client';
import reportWebVitals from './reportWebVitals';
import { HashRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import PremiumUnlockButton from './components/buttons/PremiumUnlockButton/PremiumUnlockButton';
import ArticleRecommendationContainer from './ai-recommendation/components/ArticleRecommendationContainer';
import PaymentPage from './payment/components/PaymentPage';
import SalesFunnelEmbed from './sales-funnel/components/SalesFunnelEmbed';
import AccountDropdown from './account-dropdown/components/AccountDropdown';
import ActivationPage from './subscription-activation/components/ActivationPage';
import { getCookieValue } from './utils/cookie';
import { logout } from './auth/services/AuthService';
import LatestArticles from './home/sections/LatestArticles';
import EditorPicks from './home/sections/EditorPicks';
import CategoryTypeA from './home/sections/CategoryTypeA';
import CategoryTypeC from './home/sections/CategoryTypeC';
import CategoryTypeB from './home/sections/CategoryTypeB';
import LifePodcast from './home/sections/LifePodcast';
import MostReadArticles from './home/sections/MostReadArticles';
import ProductCarousel from './home/sections/ProductCarousel';
import NewsletterBox from './home/sections/NewsletterBox';
import LoginModal from './auth/components/LoginModal';
import AuthorAndTopicBox from './below-article/AuthorAndTopicBox';
import ArticleMeta from './components/article-meta/ArticleMeta';
import MagazineFilter from './print/components/magazine-filter/MagazineFilter';
import PrintArticles from './print/components/print-articles/PrintArticles';
import ThemeSwitcher from './components/theme-switcher/ThemeSwitcher';
import AiClicker from './ai-recommendation/components/AiClicker';
import AuthorPage from './authors-archive/components/AuthorPage';
import NewsletterPage from './newsletter-landing/components/NewsletterPage';
import AuthenticationForm from './auth/components/AuthenticationForm';
import PageHeaderBlock from './components/blocks/page-header/PageHeader';
import PremiumPage from './premium-landing/components/PremiumPage';
import PostsSelection from './components/blocks/posts-selection-block/PostsSelection';
import BenefitsCards from './components/blocks/benefits-cards-block/BenefitsCards';
import TilesBlock from './e-commerce/components/TilesBlock';
import ProductsBlock from './e-commerce/components/ProductsBlock';
import DetailPage from './e-commerce/pages/DetailPage';
import BrandsPage from './e-commerce/pages/BrandsPage';
import BrandDetailPage from './e-commerce/pages/BrandDetailPage';
import GenderPage from './e-commerce/pages/GenderPage';
import CategoryPage from './e-commerce/pages/CategoryPage';
import ProductCard from './home/components/ProductCard';
import Tabs from './components/tabs/Tabs';
import VendorDetailPage from './e-commerce/pages/VendorDetailPage';

// Check if crm_user is not present, but there is a cookie set for it
if (window.country === 'cz') {
  if (!window.crm_user && getCookieValue('n_token')) {
    console.log('Logging out due to missing crm_user');
    setTimeout(() => {
      logout();
    }, 1000);
  }
}

const path = window.location.pathname;
const myAccountSlug = window.myAccountPageSlug || 'my-account';

if (path.includes(myAccountSlug)) {
  const reactAppMyAccount = document.getElementById('react-app-my-account');
  if (reactAppMyAccount) {
    createRoot(reactAppMyAccount).render(
      <HashRouter>
        <App />
      </HashRouter>
    );
  }
} else {
  document.addEventListener('DOMContentLoaded', function () {
    const authenticationForm = document.getElementById('reactAuthenticationForm');
    if (authenticationForm) {
      createRoot(authenticationForm).render(<AuthenticationForm />);
    }

    const premiumUnlockButtonElement = document.getElementById('reactPremiumBlockerButton');
    if (premiumUnlockButtonElement) createRoot(premiumUnlockButtonElement).render(<PremiumUnlockButton />);
  });
}

document.addEventListener('DOMContentLoaded', function () {
  const aiRecommendation = document.getElementById('reactAIRecommendation');
  if (aiRecommendation) createRoot(aiRecommendation).render(<ArticleRecommendationContainer />);

  const thankYouPage = document.getElementById('reactThankYouPage');
  if (thankYouPage) createRoot(thankYouPage).render(<PaymentPage />);

  const salesFunnelEmbed = document.getElementById('reactSalesFunnelEmbed');
  if (salesFunnelEmbed) createRoot(salesFunnelEmbed).render(<SalesFunnelEmbed />);

  const latestArticles = document.getElementById('reactLatestArticles');
  if (latestArticles) {
    const latestArticlesData = JSON.parse(latestArticles?.dataset?.latestArticles ?? 'null');
    createRoot(latestArticles).render(<LatestArticles articles={latestArticlesData} />);
  }

  const editorsChoice = document.getElementById('reactEditorsChoice');
  if (editorsChoice) createRoot(editorsChoice).render(<EditorPicks articles={[]} />);

  const categoryTypeAElements = document.querySelectorAll('.reactCategoryTypeA');
  if (categoryTypeAElements.length > 0) {
    categoryTypeAElements.forEach((categoryTypeA) => {
      const categoryBlockDatas = JSON.parse(categoryTypeA?.getAttribute('data-category-articles') ?? 'null');
      const sectionDatas = JSON.parse(categoryTypeA?.getAttribute('data-section-data') ?? 'null');

      createRoot(categoryTypeA).render(<CategoryTypeA articlesData={categoryBlockDatas} sectionDatas={sectionDatas} />);
    });
  }

  const categoryTypeBElements = document.querySelectorAll('.reactCategoryTypeB');
  if (categoryTypeBElements.length > 0) {
    categoryTypeBElements.forEach((categoryTypeB) => {
      const categoryBlockDatas = JSON.parse(categoryTypeB?.getAttribute('data-category-articles') ?? 'null');
      const sectionDatas = JSON.parse(categoryTypeB?.getAttribute('data-section-data') ?? 'null');

      createRoot(categoryTypeB).render(<CategoryTypeB articlesData={categoryBlockDatas} sectionDatas={sectionDatas} />);
    });
  }

  const categoryTypeCElements = document.querySelectorAll('.reactCategoryTypeC');
  if (categoryTypeCElements.length > 0) {
    categoryTypeCElements.forEach((categoryTypeC) => {
      const categoryBlockDatas = JSON.parse(categoryTypeC?.getAttribute('data-category-articles') ?? 'null');
      const sectionDatas = JSON.parse(categoryTypeC?.getAttribute('data-section-data') ?? 'null');

      createRoot(categoryTypeC).render(<CategoryTypeC articlesData={categoryBlockDatas} sectionDatas={sectionDatas} />);
    });
  }

  const podcastsSection = document.getElementById('reactLifePodcasts');
  if (podcastsSection) createRoot(podcastsSection).render(<LifePodcast />);

  const mostReadSection = document.getElementById('reactMostRead');
  if (mostReadSection) createRoot(mostReadSection).render(<MostReadArticles />);

  const productCarousel = document.querySelectorAll('.reactLifeProductCarousel');
  productCarousel.forEach((el) => {
    const products = JSON.parse(el?.getAttribute('data-react-json-prop-products') ?? 'null');
    const header = JSON.parse(el?.getAttribute('data-react-json-prop-header') ?? 'null');

    // Hide if user came from Seznam newsfeed
    if (window?.__pxPageConfig?.traffic !== 'szn_newsfeed') {
      createRoot(el).render(<ProductCarousel products={products} header={header} />);
    }
  });

  const newsletterSection = document.getElementById('reactNewsletters');
  if (newsletterSection) {
    const selectedNewsletters = JSON.parse(newsletterSection?.getAttribute('data-selected-newsletters') ?? 'null');

    const sectionData = JSON.parse(newsletterSection?.getAttribute('data-section-data') ?? 'null');

    const hasBackground = newsletterSection.getAttribute('data-has-background') === '1';

    // Hide if user came from Seznam newsfeed
    if (window?.__pxPageConfig?.traffic !== 'szn_newsfeed') {
      createRoot(newsletterSection).render(
        <NewsletterBox
          selectedNewsletters={selectedNewsletters}
          hasBackground={hasBackground}
          sectionDatas={sectionData}
        />
      );
    }
  }

  const accountDropdown = document.getElementById('reactAccountDropdownBody');
  if (accountDropdown) createRoot(accountDropdown).render(<AccountDropdown />);

  const subscriptionActivationPage = document.getElementById('reactSubscriptionActivation');
  if (subscriptionActivationPage) createRoot(subscriptionActivationPage).render(<ActivationPage />);

  const authorBoxTop = document.getElementById('reactAuthorBoxTop');
  if (authorBoxTop) createRoot(authorBoxTop).render(<AuthorAndTopicBox layout="article-top" cardsType="author" />);

  const authorBoxBottom = document.getElementById('reactAuthorBoxBottom');
  if (authorBoxBottom)
    createRoot(authorBoxBottom).render(<AuthorAndTopicBox layout="article-bottom" cardsType="author" />);

  const reactLoginModal = document.getElementById('react-login-modal');
  if (reactLoginModal) {
    createRoot(reactLoginModal).render(<LoginModal />);
  }
  const articleTermsBox = document.getElementById('reactArticleTermsBox');
  if (articleTermsBox)
    createRoot(articleTermsBox).render(<AuthorAndTopicBox layout="article-term" cardsType="topic" />);

  const articleMetaElements = document.querySelectorAll('.reactArticleMeta');
  articleMetaElements.forEach((articleMetaElement) => {
    const articleAuthors = JSON.parse(articleMetaElement?.getAttribute('data-authors') ?? 'null');

    const articleData = JSON.parse(articleMetaElement?.getAttribute('data-article') ?? 'null');

    createRoot(articleMetaElement).render(<ArticleMeta articleAuthors={articleAuthors} articleData={articleData} />);
  });

  const magazineFilter = document.querySelector('.reactMagazineFilter');
  if (magazineFilter) createRoot(magazineFilter).render(<MagazineFilter onSelectMagazine={() => {}} />);

  const magazinePostsWrapper = document.querySelector('.reactPrintArticles');
  if (magazinePostsWrapper) createRoot(magazinePostsWrapper).render(<PrintArticles />);

  const themeSwitcher = document.getElementById('react-theme-switcher');
  if (themeSwitcher) {
    createRoot(themeSwitcher).render(<ThemeSwitcher />);
  }

  const aiClicker = document.getElementById('react-ai-clicker');
  if (aiClicker) {
    createRoot(aiClicker).render(<AiClicker />);
  }

  const pageAuthor = document.querySelector('.reactPageAuthors');
  if (pageAuthor) createRoot(pageAuthor).render(<AuthorPage />);

  const pageNewsletterContent = document.querySelector('.reactPageNewsletterContent');
  if (pageNewsletterContent) createRoot(pageNewsletterContent).render(<NewsletterPage />);

  const pagePremiumContent = document.querySelector('.reactPagePremiumContent');
  if (pagePremiumContent) createRoot(pagePremiumContent).render(<PremiumPage />);

  const pageHeaderBlock = document.querySelector('.reactPageHeader');
  if (pageHeaderBlock) createRoot(pageHeaderBlock).render(<PageHeaderBlock />);

  const postsSelectionBlock = document.querySelectorAll('.reactPostsSelection');
  if (postsSelectionBlock.length > 0) {
    postsSelectionBlock.forEach((postsSelection) => {
      const categoryBlockDatas = JSON.parse(postsSelection?.getAttribute('data-category-articles') ?? 'null');
      const sectionDatas = JSON.parse(postsSelection?.getAttribute('data-section-data') ?? 'null');
      const buttonText = postsSelection.getAttribute('data-button-text') ?? '';
      const buttonLink = postsSelection.getAttribute('data-button-link') ?? '';

      createRoot(postsSelection).render(
        <PostsSelection
          articlesData={categoryBlockDatas}
          sectionDatas={sectionDatas}
          buttonText={buttonText}
          buttonLink={buttonLink}
        />
      );
    });
  }

  const benefitsCardsBlock = document.querySelectorAll('.reactBenefitsCards');
  if (benefitsCardsBlock.length > 0) {
    benefitsCardsBlock.forEach((benefitsCards) => {
      const benefitsCard = JSON.parse(benefitsCards?.getAttribute('data-benefits-cards') ?? 'null');
      const sectionDatas = JSON.parse(benefitsCards?.getAttribute('data-section-data') ?? 'null');
      const buttonText = benefitsCards.getAttribute('data-button-text') ?? '';
      const buttonLink = benefitsCards.getAttribute('data-button-link') ?? '';

      createRoot(benefitsCards).render(
        <BenefitsCards
          benefitsData={benefitsCard}
          sectionDatas={sectionDatas}
          buttonText={buttonText}
          buttonLink={buttonLink}
        />
      );
    });
  }

  /* E-commerce Section */
  const categoryPage = document.querySelector('.reactEcommerceCategoryPage');
  if (categoryPage) {
    const products = JSON.parse(categoryPage?.getAttribute('data-products') ?? 'null');

    const categoryDetails = JSON.parse(categoryPage?.getAttribute('data-category-details') ?? 'null');

    const categoryFilters = JSON.parse(categoryPage?.getAttribute('data-category-filters') ?? 'null');

    const tabs = JSON.parse(categoryPage?.getAttribute('data-tabs') ?? 'null');

    createRoot(categoryPage).render(
      <CategoryPage products={products} categoryDetails={categoryDetails} filters={categoryFilters} tabs={tabs} />
    );
  }

  const genderPage = document.querySelector('.reactEcommerceGenderPage');
  if (genderPage) {
    const products = JSON.parse(genderPage?.getAttribute('data-products') ?? 'null');

    const genderDetails = JSON.parse(genderPage?.getAttribute('data-gender-details') ?? 'null');

    const genderFilters = JSON.parse(genderPage?.getAttribute('data-gender-filters') ?? 'null');

    const tabs = JSON.parse(genderPage?.getAttribute('data-tabs') ?? 'null');

    createRoot(genderPage).render(
      <GenderPage products={products} genderDetails={genderDetails} filters={genderFilters} tabs={tabs} />
    );
  }

  const tilesBlock = document.querySelectorAll('.reactEcommerceTiles');
  if (tilesBlock.length > 0) {
    tilesBlock.forEach((block) => {
      const tiles = JSON.parse(block?.getAttribute('data-tiles') ?? 'null');
      const type = block.getAttribute('data-type');
      const sectionHeader = JSON.parse(block?.getAttribute('data-section-header') ?? 'null');

      createRoot(block).render(<TilesBlock tiles={tiles} type={type as any} sectionHeader={sectionHeader} />);
    });
  }

  const productsBlock = document.querySelectorAll('.reactEcommerceProducts');
  if (productsBlock.length > 0) {
    productsBlock.forEach((block) => {
      const sectionHeader = JSON.parse(block?.getAttribute('data-section-header') ?? 'null');
      const bottomButton = JSON.parse(block?.getAttribute('data-bottom-button') ?? 'null');
      const productAdvertisementPosition = JSON.parse(block?.getAttribute('data-advertisement-position') ?? 'null');
      const mobileView = block.getAttribute('data-mobile-view');
      const products = JSON.parse(block?.getAttribute('data-products') ?? 'null');

      console.log(products);

      createRoot(block).render(
        <ProductsBlock
          sectionHeader={sectionHeader}
          button={bottomButton}
          products={products}
          advertisementPosition={productAdvertisementPosition}
          mobileView={mobileView as any}
        />
      );
    });
  }

  const tabsBlock = document.querySelectorAll('.reactEcommerceTabs');
  if (tabsBlock.length > 0) {
    tabsBlock.forEach((block) => {
      const tabs = JSON.parse(block?.getAttribute('data-tabs') ?? 'null');
      const useHashRouting = block.getAttribute('data-use-hash-routing') === 'true';

      createRoot(block).render(
        <Tabs tabs={tabs} useHashRouting={useHashRouting} hasBottomBorder fontSize={14} fullWidth={false} />
      );
    });
  }

  const eCommerceDetailPage = document.querySelector('#reactEcommerceDetailPage');
  if (eCommerceDetailPage) {
    const productData = JSON.parse(eCommerceDetailPage?.getAttribute('data-product-detail') ?? 'null');

    const recommendedProducts = JSON.parse(eCommerceDetailPage?.getAttribute('data-recommended-products') ?? 'null');

    createRoot(eCommerceDetailPage).render(<DetailPage {...productData} recommendedProducts={recommendedProducts} />);
  }

  const eCommerceBrandsPage = document.querySelector('#reactEcommerceBrandsPage');
  if (eCommerceBrandsPage) {
    const brandsData = JSON.parse(eCommerceBrandsPage?.getAttribute('data-brands') ?? 'null');

    createRoot(eCommerceBrandsPage).render(<BrandsPage brands={brandsData} />);
  }

  const eCommerceBrandDetailPage = document.querySelector('#reactEcommerceBrandDetailPage');
  if (eCommerceBrandDetailPage) {
    const brandDetailData = JSON.parse(eCommerceBrandDetailPage?.getAttribute('data-brand-detail') ?? 'null');
    const products = JSON.parse(eCommerceBrandDetailPage?.getAttribute('data-products') ?? 'null');
    const categories = JSON.parse(eCommerceBrandDetailPage?.getAttribute('data-categories') ?? 'null');
    const brandLogo = JSON.parse(eCommerceBrandDetailPage?.getAttribute('data-brand-logo') ?? 'null');
    const brandId = JSON.parse(eCommerceBrandDetailPage?.getAttribute('data-brand-id') ?? 'null');

    createRoot(eCommerceBrandDetailPage).render(
      <BrandDetailPage
        {...brandDetailData}
        products={products}
        categories={categories}
        logo={brandLogo}
        brandId={brandId}
      />
    );
  }

  const eCommerceVendorDetailPage = document.querySelector('#reactEcommerceVendorDetailPage');
  if (eCommerceVendorDetailPage) {
    const vendorDetailData = JSON.parse(eCommerceVendorDetailPage?.getAttribute('data-vendor-detail') ?? 'null');
    const products = JSON.parse(eCommerceVendorDetailPage?.getAttribute('data-products') ?? 'null');
    const categories = JSON.parse(eCommerceVendorDetailPage?.getAttribute('data-categories') ?? 'null');
    const vendorLogo = JSON.parse(eCommerceVendorDetailPage?.getAttribute('data-vendor-logo') ?? 'null');
    const vendorId = JSON.parse(eCommerceVendorDetailPage?.getAttribute('data-vendor-id') ?? 'null');

    createRoot(eCommerceVendorDetailPage).render(
      <VendorDetailPage
        {...vendorDetailData}
        products={products}
        categories={categories}
        logo={vendorLogo}
        vendorId={vendorId}
      />
    );
  }

  const ecommerceArticleProducts = document.querySelectorAll('.reactEcommerceArticleProducts');
  if (ecommerceArticleProducts.length > 0) {
    ecommerceArticleProducts.forEach((block) => {
      const products = JSON.parse(block?.getAttribute('data-products') ?? 'null');

      if (products.length === 1) {
        createRoot(block).render(<ProductCard {...products[0]} />);
      } else {
        createRoot(block).render(<ProductsBlock products={products} grid={'double'} />);
      }
    });
  }

  /* End of E-commerce Section */
});

reportWebVitals();
